import api from './axiosApi'
import type {
  Attribute,
  AttributeFormData,
  AttributeType,
} from '@/types/attribute'

interface SkuAttributeApiItem {
  id?: string | number
  code?: string
  name?: string | null
  description?: string | null
  category_id?: string | null
  category_name?: string | null
  category?: string | null
  subcategory_id?: string | null
  subcategory?: string | null
  subcategory_name?: string | null
  attribute_type?: string | null
  is_required?: boolean | null
  dropdown_mode?: 'FIXED' | 'EXTENSIBLE' | null
  dropdown_options?: string[] | null
  created_at?: string | null
}

interface SkuAttributesListResponse {
  data?: SkuAttributeApiItem[]
  pagination?: {
    skip: number
    limit: number
    total: number
    has_more: boolean
  }
}

export interface SkuAttributeCategoryRead {
  id: string
  name: string
  parent_id: string | null
  parent_name?: string | null
  display_order?: number
  is_active?: boolean
}

interface SkuAttributeCategoriesListResponse {
  data?: Array<Partial<SkuAttributeCategoryRead>>
}

interface CreateSkuAttributePayload {
  code: string
  name: string
  description: string | null
  attribute_type: AttributeType
  category_id?: string
  category_name: string
  subcategory_id?: string
  subcategory_name: string
  dropdown_options?: string[]
  dropdown_mode?: 'FIXED' | 'EXTENSIBLE'
  is_required: boolean
  default_value: string | null
  display_order: number
}

interface UpdateSkuAttributePayload {
  name: string
  description: string | null
  category_id: string | null
  category_name: string
  subcategory_id: string | null
  subcategory_name: string
  dropdown_options?: string[]
  dropdown_mode?: 'FIXED' | 'EXTENSIBLE'
  is_required: boolean
  default_value: string | null
  display_order: number
  is_active: boolean
}

function normalizeAttributeType(
  value: string | null | undefined
): AttributeType {
  const normalized = value?.trim().toUpperCase()
  if (
    normalized === 'TEXT' ||
    normalized === 'NUMBER' ||
    normalized === 'DATE' ||
    normalized === 'DROPDOWN' ||
    normalized === 'BOOLEAN'
  ) {
    return normalized
  }

  return 'TEXT'
}

function toDisplayValue(value: string | null | undefined): string {
  const normalized = value?.trim()
  return normalized && normalized.length > 0 ? normalized : '-'
}

function mapSkuAttributeToFormAttribute(
  item: SkuAttributeApiItem,
  index: number
): Attribute {
  return {
    id: String(item.id ?? item.code ?? `attr-${index}`),
    category_id: item.category_id ?? null,
    subcategory_id: item.subcategory_id ?? null,
    name: toDisplayValue(item.name),
    description: toDisplayValue(item.description),
    category: toDisplayValue(
      item.category_name ?? item.category ?? item.category_id
    ),
    subcategory: toDisplayValue(
      item.subcategory_name ?? item.subcategory ?? item.subcategory_id
    ),
    attribute_type: normalizeAttributeType(item.attribute_type),
    required: typeof item.is_required === 'boolean' ? item.is_required : null,
    locked:
      item.dropdown_mode === 'FIXED'
        ? true
        : item.dropdown_mode === 'EXTENSIBLE'
          ? false
          : null,
    defined_values: Array.isArray(item.dropdown_options)
      ? item.dropdown_options.filter(option => Boolean(option?.trim()))
      : [],
    created_at: toDisplayValue(item.created_at),
  }
}

export async function getSkuAttributes(skip = 0, limit = 100) {
  const response = await api.get<
    SkuAttributesListResponse | SkuAttributeApiItem[]
  >('/admin/sku-attributes', {
    params: {
      skip,
      limit,
      _ts: Date.now(),
    },
  })

  const responseData = response.data
  const list = Array.isArray(responseData)
    ? responseData
    : (responseData.data ?? [])

  return list.map((item, index) => mapSkuAttributeToFormAttribute(item, index))
}

export async function getSkuAttributeCategories(parentId?: string | null) {
  const params: Record<string, string | boolean> = {
    is_active: true,
  }

  if (parentId !== undefined) {
    if (parentId === null) {
      params.top_level = true
    } else {
      params.parent_id = parentId
    }
  }

  const response = await api.get<
    | SkuAttributeCategoriesListResponse
    | Array<Partial<SkuAttributeCategoryRead>>
  >('/admin/sku-attributes/categories', {
    params,
  })

  const responseData = response.data
  const list = Array.isArray(responseData)
    ? responseData
    : (responseData.data ?? [])

  return list
    .filter(
      (item): item is Partial<SkuAttributeCategoryRead> & { id: string } =>
        typeof item.id === 'string'
    )
    .map(item => ({
      id: item.id,
      name: item.name?.trim() || 'Main',
      parent_id: item.parent_id ?? null,
      parent_name: item.parent_name ?? null,
      display_order: item.display_order,
      is_active: item.is_active,
    }))
}

function toAttributeCode(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  return (normalized || 'attribute').slice(0, 100)
}

export function mapAttributeFormToCreatePayload(
  data: AttributeFormData
): CreateSkuAttributePayload {
  const isDropdown = data.attribute_type === 'DROPDOWN'
  const categoryName = data.category.trim() || 'Main'
  const subcategoryName = data.subcategory.trim() || 'Main'
  const categoryId = data.category_id?.trim()
  const subcategoryId = data.subcategory_id?.trim()
  const definedValues = isDropdown
    ? data.defined_values.map(option => option.trim()).filter(Boolean)
    : []

  return {
    code: toAttributeCode(data.name),
    name: data.name.trim(),
    description: data.description.trim() || null,
    ...(categoryId ? { category_id: categoryId } : {}),
    category_name: categoryName,
    ...(subcategoryId ? { subcategory_id: subcategoryId } : {}),
    subcategory_name: subcategoryName,
    attribute_type: data.attribute_type,
    ...(isDropdown
      ? {
          dropdown_options: definedValues,
          dropdown_mode: data.locked
            ? ('FIXED' as const)
            : ('EXTENSIBLE' as const),
        }
      : {}),
    is_required: data.required,
    default_value: null,
    display_order: 0,
  }
}

export function mapAttributeFormToUpdatePayload(
  data: AttributeFormData
): UpdateSkuAttributePayload {
  const isDropdown = data.attribute_type === 'DROPDOWN'
  const categoryName = data.category.trim() || 'Main'
  const subcategoryName = data.subcategory.trim() || 'Main'
  const definedValues = isDropdown
    ? data.defined_values.map(option => option.trim()).filter(Boolean)
    : []

  return {
    name: data.name.trim(),
    description: data.description.trim() || null,
    category_id: data.category_id ?? null,
    category_name: categoryName,
    subcategory_id: data.subcategory_id ?? null,
    subcategory_name: subcategoryName,
    ...(isDropdown
      ? {
          dropdown_options: definedValues,
          dropdown_mode: data.locked
            ? ('FIXED' as const)
            : ('EXTENSIBLE' as const),
        }
      : {}),
    is_required: data.required,
    default_value: null,
    display_order: 0,
    is_active: true,
  }
}

export async function createSkuAttribute(data: AttributeFormData) {
  const payload = mapAttributeFormToCreatePayload(data)
  const response = await api.post('/admin/sku-attributes', payload)
  return response.data
}

export async function updateSkuAttribute(id: string, data: AttributeFormData) {
  const payload = mapAttributeFormToUpdatePayload(data)
  const response = await api.patch(`/admin/sku-attributes/${id}`, payload)
  return response.data
}

export async function deleteSkuAttribute(id: string) {
  const response = await api.delete(`/admin/sku-attributes/${id}`)
  return response.data
}
